import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import ProviderService from '../../services/providerservice';
import SubServiceTypeService from '../../services/subServiceTypeService';
import SearchableSelect from '../../components/common/searchable-select';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 8;

const SERVICE_TYPE_TRANSACTION = 3;

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/** คำนวณ page numbers ที่แสดงรอบ current page (±2) */
const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ─────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────

/** Modal เพิ่มประเภทบริการ (SubServiceType) */
const SubServiceTypeModal = ({ onClose, onSave }) => {
    const [form, setForm] = useState({ name: '', code: '' });

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => setTimeout(() => onSave(form), 0);

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>เพิ่มประเภทบริการ New Service</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form">
                            <div className="mc-form-group">
                                <label>ชื่อประเภท</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>Create</button>
                </div>
            </div>
        </div>
    );
};

/** Modal เพิ่ม/แก้ไข Provider */
const ProviderModal = ({ mode, data, subServiceTypes, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th:             data?.name_th             || '',
        name_en:             data?.name_en             || '',
        id_sub_service_type: data?.id_sub_service_type || '',
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => setTimeout(() => onSave(form), 0);

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Edit' : 'เพิ่มผู้ให้บริการใหม่ New Provider'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>ประเภทบริการ</label>
                                <SearchableSelect
                                    id="provider-sub-service-type"
                                    name="id_sub_service_type"
                                    value={form.id_sub_service_type}
                                    onChange={(e) => handleChange('id_sub_service_type', Number(e.target.value))}
                                    // ── FIX: ใช้ st.id ตรงๆ (id ของ m_sub_service_type) แทนการแปลงผ่าน toEnumValue ──
                                    options={subServiceTypes.map((st) => ({ value: st.id, label: st.name }))}
                                    placeholder="Select"
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>ชื่อผู้ให้บริการ</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_th}
                                    onChange={(e) => handleChange('name_th', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Modal เพิ่ม/แก้ไข Branch ของ Provider */
const BranchModal = ({ mode, data, providerName, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th: data?.name_th || '',
        name_en: data?.name_en || '',
        map:     data?.map    || '',
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => setTimeout(() => onSave(form), 0);

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไขสาขา' : 'เพิ่มสาขา'}</h3>
                </div>
                {providerName && (
                    <div className="mc-modal-bank-name">
                        <span className="mc-modal-bank-badge">{providerName}</span>
                    </div>
                )}
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-group mb-8">
                            <label>ชื่อสาขา</label>
                            <input
                                type="text"
                                placeholder="Input"
                                value={form.name_th}
                                onChange={(e) => handleChange('name_th', e.target.value)}
                            />
                        </div>
                        <div className="mc-form-group">
                            <label>Map</label>
                            <input
                                type="text"
                                placeholder="Input"
                                value={form.map}
                                onChange={(e) => handleChange('map', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminServiceProvider = () => {
    // ── Data state ──
    const [subServiceTypes, setSubServiceTypes] = useState([]);
    const [activeTab, setActiveTab]             = useState(null);
    const [providers, setProviders]             = useState([]);
    const [branches, setBranches]               = useState({}); // { providerId: branch[] }
    const [expanded, setExpanded]               = useState({}); // { providerId: boolean }

    // ── UI state ──
    const [search, setSearch]       = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modal, setModal]         = useState(null); // null | { type, mode?, data?, providerId?, providerName? }
    const [newProviderId, setNewProviderId] = useState(null); // highlight หลัง create
    const [newBranchId, setNewBranchId]    = useState(null);

    // ── Refs ──
    const highlightRef = useRef(null);

    // โหลด sub service types ตอน mount
    useEffect(() => {
        loadSubServiceTypes();
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    // โหลด providers เมื่อ tab เปลี่ยน (watch id เพื่อป้องกัน infinite loop)
    useEffect(() => {
        if (!activeTab) return;
        loadProviders(activeTab);
        setSearch('');
        setCurrentPage(1);
    }, [activeTab?.id]);

    // ─────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────

    /** highlight row ที่เพิ่งสร้าง 10 วิ แล้วล้าง */
    const startHighlight = (setter, id) => {
        setter(id);
        if (highlightRef.current) clearTimeout(highlightRef.current);
        highlightRef.current = setTimeout(() => setter(null), 10000);
    };

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadSubServiceTypes = async () => {
        try {
            const resp = await SubServiceTypeService.SearchSubServiceType({
                id_service_type: SERVICE_TYPE_TRANSACTION,
                status: 1,
            });
            if (!resp.data.is_success || !resp.data.result) return;
            const sorted = [...resp.data.result].sort((a, b) => a.id - b.id);
            setSubServiceTypes(sorted);
            if (sorted.length > 0) setActiveTab(sorted[0]);
        } catch (err) { console.error(err); }
    };

    const loadProviders = async (subServiceType) => {
        try {
            const resp = await ProviderService.SearchProvider({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;

            // ── FIX: filter ด้วย id ของ m_sub_service_type ตรงๆ (ไม่ผ่าน toEnumValue อีกต่อไป) ──
            // ── filter ที่ frontend เพราะ API ไม่รองรับ filter หลาย field พร้อมกัน ──
            const filtered = resp.data.result.filter(
                (p) => p.id_service_type === SERVICE_TYPE_TRANSACTION &&
                       Number(p.id_sub_service_type) === Number(subServiceType.id)
            );
            const sorted = [...filtered].sort((a, b) =>
                (a.name_th || '').localeCompare(b.name_th || '', 'th')
            );
            setProviders(sorted);

            // โหลด branches ทั้งหมดพร้อมกัน (parallel)
            const branchResponses = await Promise.all(
                sorted.map((p) => ProviderService.SearchProviderBranch({ id_provider: p.id, status: 1 }))
            );
            const branchMap = {};
            sorted.forEach((p, i) => {
                branchMap[p.id] = branchResponses[i].data.result || [];
            });
            setBranches(branchMap);
            setExpanded({});
        } catch (err) { console.error(err); }
    };

    /** โหลด branches ของ provider เดียว (ใช้หลัง add/edit/delete branch) */
    const loadBranches = async (providerId) => {
        try {
            const resp = await ProviderService.SearchProviderBranch({ id_provider: providerId, status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            setBranches((prev) => ({ ...prev, [providerId]: resp.data.result }));
        } catch (err) { console.error(err); }
    };

    /** toggle expand/collapse row พร้อม lazy load branches */
    const toggleExpand = (id) => {
        setExpanded((prev) => {
            const isOpening = !prev[id];
            if (isOpening && !branches[id]) loadBranches(id);
            return { ...prev, [id]: isOpening };
        });
    };

    // ─────────────────────────────────────────────────────
    // Handlers — SubServiceType
    // ─────────────────────────────────────────────────────

    const handleSaveSubServiceType = async (form) => {
        setModal(null);
        const confirmed = await showConfirm({
            title: 'ยืนยันการเพิ่มประเภทใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await SubServiceTypeService.CreateSubServiceType({
                code:            form.code,
                name:            form.name,
                id_service_type: SERVICE_TYPE_TRANSACTION,
                status:          1,
            });
            hideLoading();
            await showSuccessDetail({ icon: 'mdi mdi-check-circle', title: 'เพิ่มประเภทสำเร็จแล้ว' });
            await loadSubServiceTypes();
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers — Provider
    // ─────────────────────────────────────────────────────

    const handleSaveProvider = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขผู้ให้บริการ' : 'ยืนยันการเพิ่มผู้ให้บริการใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th:             form.name_th,
                name_en:             form.name_en || form.name_th,
                id_service_type:     SERVICE_TYPE_TRANSACTION,
                id_sub_service_type: form.id_sub_service_type,
                status:              1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await ProviderService.UpdateProvider(currentData?.id, payload);
            } else {
                const resp = await ProviderService.CreateProvider(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขผู้ให้บริการสำเร็จแล้ว' : 'เพิ่มผู้ให้บริการสำเร็จแล้ว',
            });

            await loadProviders(activeTab);
            if (currentMode === 'add' && createdId) startHighlight(setNewProviderId, createdId);
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDeleteProvider = async (provider) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบ',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await ProviderService.DeleteProvider(provider.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบผู้ให้บริการออกแล้ว' });
            loadProviders(activeTab);
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers — Branch
    // ─────────────────────────────────────────────────────

    const handleSaveBranch = async (form) => {
        const { mode: currentMode, data: currentData, providerId } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขสาขา' : 'ยืนยันการเพิ่มสาขาใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th:             form.name_th,
                name_en:             form.name_en || form.name_th,
                map:                 form.map || '',
                id_provider:         providerId,
                id_service_type:     SERVICE_TYPE_TRANSACTION,
                id_sub_service_type: activeTab?.id || 0,
                status:              1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await ProviderService.UpdateProviderBranch(currentData?.id, payload);
            } else {
                const resp = await ProviderService.CreateProviderBranch(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขสาขาสำเร็จแล้ว' : 'เพิ่มสาขาสำเร็จแล้ว',
            });

            await loadBranches(providerId);
            setExpanded((prev) => ({ ...prev, [providerId]: true }));
            if (currentMode === 'add' && createdId) startHighlight(setNewBranchId, createdId);
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDeleteBranch = async (branch, providerId) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบสาขา',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await ProviderService.DeleteProviderBranch(branch.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบสาขาออกแล้ว' });
            loadBranches(providerId);
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Derived data (filter + pagination)
    // ─────────────────────────────────────────────────────

    const filtered = providers.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.name_th || '').toLowerCase().includes(q) ||
            (row.name_en || '').toLowerCase().includes(q)
        );
    });

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    return (
        <>
            <div className="mc-page">
                {/* ── Header ── */}
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Service Provider</h2>
                        <p className="mc-subtitle">รายชื่อผู้ให้บริการ, ชำระบริการ</p>
                    </div>
                    <button
                        className="mc-btn mc-btn--new"
                        onClick={() => setModal({ mode: 'add', type: 'provider' })}
                    >
                        <i className="ri-add-line" /> New Provider
                    </button>
                </div>

                <div className="mc-card">
                    {/* ── Tabs: SubServiceType ── */}
                    <div className="sp-tabs">
                        <div className="sp-tabs__list">
                            {subServiceTypes.map((st) => (
                                <button
                                    key={st.id}
                                    className={`sp-tab ${activeTab?.id === st.id ? 'sp-tab--active' : ''}`}
                                    onClick={() => setActiveTab(st)}
                                >
                                    {st.name}
                                </button>
                            ))}
                        </div>
                        <div className="sp-tabs__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    {/* ── Toolbar: search ── */}
                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="ค้นหา"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>No.</th>
                                    <th>ชื่อผู้ให้บริการ</th>
                                    <th style={{ width: 80 }}>สาขา</th>
                                    <th style={{ width: 130 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((provider, idx) => (
                                        <React.Fragment key={provider.id}>
                                            {/* ── Provider row ── */}
                                            <tr className={provider.id === newProviderId ? 'mc-row-new' : ''}>
                                                <td className="mc-col-no">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <button
                                                            className="mc-expand-btn"
                                                            onClick={() => toggleExpand(provider.id)}
                                                        >
                                                            <i className={expanded[provider.id] ? 'ri-arrow-down-s-fill' : 'ri-arrow-right-s-fill'} />
                                                        </button>
                                                        {(safePage - 1) * ROWS_PER_PAGE + idx + 1}
                                                    </div>
                                                </td>
                                                <td>{provider.name_th}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {branches[provider.id]?.length ?? '-'}
                                                </td>
                                                <td>
                                                    <div className="mc-actions">
                                                        <button
                                                            className="mc-action-btn mc-action-btn--edit"
                                                            onClick={() => setModal({ mode: 'edit', type: 'provider', data: provider })}
                                                        >
                                                            <i className="ri-pencil-line" />
                                                        </button>
                                                        <button
                                                            className="mc-action-btn mc-action-btn--delete"
                                                            onClick={() => handleDeleteProvider(provider)}
                                                        >
                                                            <i className="ri-delete-bin-line" />
                                                        </button>
                                                        <button
                                                            className="mc-action-btn mc-action-btn--add"
                                                            onClick={() => setModal({
                                                                mode: 'add',
                                                                type: 'branch',
                                                                providerId:   provider.id,
                                                                providerName: provider.name_th,
                                                            })}
                                                        >
                                                            <i className="ri-add-line" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* ── Branch rows (แสดงเมื่อ expand) ── */}
                                            {expanded[provider.id] && (branches[provider.id] || []).map((branch) => (
                                                <tr
                                                    key={branch.id}
                                                    className={`mc-branch-row${branch.id === newBranchId ? ' mc-row-new' : ''}`}
                                                >
                                                    <td />
                                                    <td className="mc-branch-name">
                                                        <i className="ri-corner-down-right-line" />
                                                        {branch.name_th}
                                                    </td>
                                                    <td>
                                                        {branch.map && (
                                                            <a
                                                                href={branch.map}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mc-map-link"
                                                            >
                                                                ดูแผนที่
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="mc-actions">
                                                            <button
                                                                className="mc-action-btn mc-action-btn--edit"
                                                                onClick={() => setModal({
                                                                    mode: 'edit',
                                                                    type: 'branch',
                                                                    data:         branch,
                                                                    providerId:   provider.id,
                                                                    providerName: provider.name_th,
                                                                })}
                                                            >
                                                                <i className="ri-pencil-line" />
                                                            </button>
                                                            <button
                                                                className="mc-action-btn mc-action-btn--delete"
                                                                onClick={() => handleDeleteBranch(branch, provider.id)}
                                                            >
                                                                <i className="ri-delete-bin-line" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* ── Modals ── */}
            {modal?.type === 'subServiceType' && (
                <SubServiceTypeModal
                    onClose={() => setModal(null)}
                    onSave={handleSaveSubServiceType}
                />
            )}
            {modal?.type === 'provider' && (
                <ProviderModal
                    mode={modal.mode}
                    data={modal.data}
                    subServiceTypes={subServiceTypes}
                    onClose={() => setModal(null)}
                    onSave={handleSaveProvider}
                />
            )}
            {modal?.type === 'branch' && (
                <BranchModal
                    mode={modal.mode}
                    data={modal.data}
                    providerName={modal.providerName}
                    onClose={() => setModal(null)}
                    onSave={handleSaveBranch}
                />
            )}
        </>
    );
};

export default AdminServiceProvider;